import { FC, ReactNode, useCallback, useEffect } from 'react';
import { findNavigationItemInMenu } from './findNavigationItemInMenu';
import { navigationBreadcrumbsAtom, navigationCurrentItemAtom, navigationItemsAtom } from '@/lib/store/Contentstore';
import { NavigationItemResponse, NavigationProps } from './types';
import { navigationQueryOptions } from './navigationQuery';
import { toPathKey } from '@/features/basic/locationKey';
import { useAtom, useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import NavigationList from './NavigationList';
import styles from './Navigation.module.css';

export const Navigation: FC<NavigationProps> = ({ callback }): ReactNode => {
  const { data: navigationData, isPending } = useQuery(navigationQueryOptions);
  const [navigationItems, setNavigationItems] = useAtom(navigationItemsAtom);
  const [currentItem, setCurrentItem] = useAtom(navigationCurrentItemAtom);
  const setBreadcrumbs = useSetAtom(navigationBreadcrumbsAtom);
  const nav = useNavigate();
  const routerlocation = useLocation();
  const findItemInMenu = useCallback(findNavigationItemInMenu, []);

  useEffect(() => {
    if (navigationData) {
      setNavigationItems(navigationData);
    }
  }, [navigationData, setNavigationItems]);

  useEffect(() => {
    const itemInMenu = findItemInMenu(navigationItems, toPathKey(document.location.pathname));
    if (!itemInMenu) {
      setBreadcrumbs([]);
      setCurrentItem(undefined);
    }
    else {
      setBreadcrumbs(itemInMenu.breadcrumbs);
      setCurrentItem(itemInMenu as NavigationItemResponse);
    }
  }, [routerlocation, navigationItems, findItemInMenu, setBreadcrumbs, setCurrentItem]);

  const handleCallback = (item: NavigationItemResponse) => {
    if (callback) callback(item);
    nav(item.url);
  };

  return (
    <nav className={styles.nav} aria-label="navigation">
      {isPending && navigationItems.length === 0 && (
        <div className={styles.loading} role="status" aria-live="polite" aria-label="Laddar navigation">
          <span className={styles.loadingLine} />
          <span className={styles.loadingLine} />
          <span className={styles.loadingLine} />
          <span className={styles.loadingLine} />
        </div>
      )}
      <NavigationList items={navigationItems} expanded={true} callback={handleCallback} currentItem={currentItem} />
    </nav>
  );
};

export default Navigation;
